import React, { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'lodash';

import { ImagePreview } from './ImagePreview';
import ImageClusterPreview from './ImageClusterPreview';
import ImageTopSimilarityFinder from './ImageTopSimilarityFinder';
import ImagePCAScatterPlot from '../embedding-explorer/ImagePCAScatterPlot';
import { nearestNeighborEdges } from '../embedding-explorer/embeddingExplorerUtils';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { IconButton } from '../common/IconButton';
import { clearCacheByUrlAndId } from '../common/react-hooks/useGetCachedDocIdBatched';
import BadgeColorHash from '../common/BadgeColorHash';
import MakeImg from '../common/MakeImg';
import SimilarityBadge, { simColor } from '../common/SimilarityBadge';
import { CompareImagePreview } from './CompareImagePreview';

const SimBadge = React.memo(function SimBadge({ value }) {
  return <SimilarityBadge value={value} title="best-match similarity [0,1]" />;
});

/** Median similarity of the image to the whole opposite cluster — a sturdier "really belongs" signal. */
const MedianBadge = React.memo(function MedianBadge({ value }) {
  if (value == null) return null;
  return (
    <span className="badge badge-light border ml-1" style={{ color: simColor(value) }} title="median similarity to the whole other cluster [0,1]">
      x̃ {Number(value).toFixed(3)}
    </span>
  );
});

const Thumb = React.memo(function Thumb({ img, compareUrl, size = '50', highlight = false, children = null, ...other }) {
  const sizeProp = size === '200' ? { img200: true } : size === '100' ? { img100: true } : { img50: true };
  return (
    <CompareImagePreview
      {...sizeProp}
      compareUrl={compareUrl}
      cover
      url={img.url}
      context={img.vehicle}
      children={children}
      className={`mr-1 align-middle ${highlight ? 'border border-primary' : 'bg-light'}`}
      {...other}
    />
  );
});

/**
 * One cluster's column: its tag, a row with its two most-similar images (~400px), then the full
 * cluster preview (fetched by id). `topImages` is [{ img, similarity }].
 */
const ClusterColumn = React.memo(function ClusterColumn({ summary, color, topImages, compareImages, isLeft = false, hideModel = false }) {
  return (
    <div className={`${isLeft ? ' text-right pr-2' : 'pl-2'}`}>
      <div className={`d-flex ${isLeft ? ' justify-content-end' : ''}`}>
        {isLeft && !hideModel ? <ClusterTag summary={summary} color={color} /> : null}
        {topImages.map(({ img, similarity, median }, k) => (
          <div key={(img && img.imgId) || k} className="text-center m-05">
            <Thumb img={img} size="200" highlight left={!isLeft} compareUrl={compareImages[k]?.img?.url}>
              {similarity != null && <div><SimBadge value={similarity} /><MedianBadge value={median} /></div>}
            </Thumb>
          </div>
        ))}
        {!isLeft && !hideModel ? <ClusterTag summary={summary} color={color} /> : null}
      </div>
      <ImageClusterPreview clusterId={summary.clusterId} size="50" maxImages={10} />
    </div>
  );
});

const ClusterTag = React.memo(function ClusterTag({ summary, color }) {
  return (
    <span className={`px-1 rounded text-center mr-2 mb-2 ${color}`} style={{ alignSelf: 'start' }}>
      <strong>
        <MakeImg modelId={summary.modelId} className='mr-1' />
        {summary.modelId || '—'}
      </strong>

      <div>
        <BadgeColorHash id={summary.clusterId} />
      </div>

      <strong className="text-white text-shadow">{summary.totalImages} imgs</strong>
    </span>
  );
});

/** Groups suggestion descriptors by their FROM (removed) cluster, preserving first-seen order. */
function groupByFromOrdered(items) {
  const order = [];
  const byFrom = new Map();
  items.forEach(it => {
    if (!byFrom.has(it.fromId)) { byFrom.set(it.fromId, []); order.push(it.fromId); }
    byFrom.get(it.fromId).push(it);
  });
  return order.map(fromId => ({ fromId, items: byFrom.get(fromId) }));
}

/**
 * How many candidates a group shows by default: candidates from the top (already
 * sorted by descending similarity) until the first >0.1 drop between consecutive
 * candidates — beyond that they're clearly worse and start collapsed. Always ≥1.
 */
function defaultVisibleCount(sortedItems) {
  let n = 1;
  while (n < sortedItems.length) {
    const prev = sortedItems[n - 1].similarity != null ? sortedItems[n - 1].similarity : 0;
    const cur = sortedItems[n].similarity != null ? sortedItems[n].similarity : 0;
    if (prev - cur > 0.01) break;
    n++;
  }
  return n;
}

/**
 * Splits a similarity-sorted group into the visible rows and the rows collapsed behind
 * "See K more". A group can now mix same-model (within-cluster) and different-model
 * suggestions for the same FROM cluster; a different-model match often scores highest and
 * would push every safer same-model option below the cliff. So if the default visible slice
 * has no same-model option, we promote the best same-model one into view — there's always at
 * least one within-cluster choice visible whenever one exists.
 */
function splitVisibleCollapsed(sortedItems) {
  const visible = sortedItems.slice(0, defaultVisibleCount(sortedItems));
  const collapsed = sortedItems.slice(visible.length);
  if (!visible.some(d => d.sameModel)) {
    const idx = collapsed.findIndex(d => d.sameModel);
    if (idx !== -1) visible.push(...collapsed.splice(idx, 1));
  }
  return { visible, collapsed };
}

/**
 * Wraps the merge rows that share one FROM cluster. When more than one merge is
 * suggested for that cluster the merges are contradictory (a cluster can only be
 * merged into a single destination), so they're boxed together and the rows enforce
 * "at most one selected" via per-FROM exclusivity in the toggle handler. Candidates
 * that fall off the similarity cliff start collapsed behind a "See K more" toggle.
 */
function GroupBlock({ multi, leftId, totalCount, visible, collapsed }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = collapsed.length > 0;
  const body = <>
    {visible}
    {hasMore && !expanded && (
      <div className="p-2 text-center">
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpanded(true)}>
          See {collapsed.length} more candidate{collapsed.length === 1 ? '' : 's'}
        </button>
      </div>
    )}
    {hasMore && expanded && collapsed}
  </>;

  if (!multi)
    return body;

  return <div className="border border-2 border-secondary rounded bg-light mb-2">
    <div className="px-2 pt-1 pb-1 small bg-dark font-weight-bold text-warning d-flex align-items-center flex-wrap">
      ⚠ Cluster with {totalCount} suggested merges — choose at MOST one
      <span className="ml-2"><BadgeColorHash id={leftId} /></span>
    </div>
    {body}
  </div>;
}


/**
 * PCA scatter of two clusters' image embeddings drawn together, points color-hashed by
 * cluster id (same hash family as the BadgeColorHash tags), so the user can eyeball whether
 * the clusters overlap (true duplicates) or form separate blobs before merging.
 */
function ClusterPairPCAGraph({ clusters }) {
  const { page } = useContext(LegoAdminPageContext);
  const [loaded, setLoaded] = useState(null); // [{ summary, items, total }] once every cluster resolves
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const embeddingSvc = page.service('services/data/fusebox-model-embeddings');
    Promise.all(clusters.map(s => embeddingSvc.find({ query: { clusterId: s.clusterId } })))
      .then(results => {
        if (cancelled) return;
        setLoaded(results.map((res, i) => ({
          summary: clusters[i],
          total: res?.totalImages || 0,
          items: (res?.items || [])
            .filter(it => it.imgUrl && Array.isArray(it.embedding) && it.embedding.length)
            .map(it => ({ imgId: String(it.imgId), imgUrl: it.imgUrl, vector: it.embedding, clusterId: it.clusterId })),
        })));
      })
      .catch(err => { if (!cancelled) setError(String(err?.message || err)); });
    return () => { cancelled = true; };
  }, [page, clusters]);

  // Black-bordered points: images whose nearest neighbor lies in the other cluster (and whose own
  // cluster has ≥2 embedded images). Many of them = the clusters interleave (likely duplicates).
  const { items, nnEdges } = useMemo(() => {
    const items = (loaded || []).flatMap(c => c.items);
    const counts = _.countBy(items, 'clusterId');
    const nnEdges = nearestNeighborEdges(items);
    nnEdges.forEach(e => {
      const a = items[e.from];
      a.crossCluster = counts[a.clusterId] >= 2 && a.clusterId !== items[e.to].clusterId;
    });
    return { items, nnEdges };
  }, [loaded]);

  if (error) return <div className="text-danger p-3">Failed to load embeddings: {error}</div>;
  if (!loaded) return <div className="text-secondary p-3">Loading embeddings for PCA…</div>;
  if (!items.length) return <div className="text-secondary p-3">No embeddings available for these clusters' images.</div>;

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', height: '100%' }}>
      <div className="d-flex align-items-center flex-wrap px-2 pt-1" style={{ gap: '16px' }}>
        {loaded.map(({ summary, items: clusterItems, total }) => (
          <span key={summary.clusterId}>
            <BadgeColorHash id={summary.clusterId} />
            <span className="ml-1 small">
              <MakeImg modelId={summary.modelId} className="mr-1" />
              <strong>{summary.modelId || '—'}</strong> · {clusterItems.length}/{total} imgs embedded
            </span>
          </span>
        ))}
      </div>
      <ImagePCAScatterPlot items={items} nnEdges={nnEdges} title="" />
    </div>
  );
}

/** One merge suggestion: a mark-for-merge checkbox + the FROM → INTO cluster columns. */
const MergeRow = React.memo(function MergeRow({ selected, onToggle, rowKey, grouped, defaultBg = 'bg-light', headerExtras = null, index, leftSummary, leftImages, rightSummary, rightImages, similarity }) {
  const {page} = useContext(LegoAdminPageContext);

  // Open the embedding-similarity explorer (same as the cluster manager) for a given image.
  const openSimilarImagesModal = useCallback((imgId) => {
    if (!imgId) return;
    page.openModal(<ImageTopSimilarityFinder imgId={imgId} showControls={true} />, { title: `Images similar to cluster's first image`, fullScreen: true });
  }, [page]);

  // PCA scatter of both clusters' embeddings together, to eyeball if they form one blob or two.
  const openPcaGraphModal = useCallback(() => {
    page.openModal(
      <ClusterPairPCAGraph clusters={[leftSummary, rightSummary]} />,
      {
        title: <>PCA of cluster embeddings <BadgeColorHash id={leftSummary.clusterId} /> vs <BadgeColorHash id={rightSummary.clusterId} /></>,
        fullScreenFixed: true,
      },
    );
  }, [page, leftSummary, rightSummary]);

  const bg = selected ? 'bg-light-success' : defaultBg;

  const rowClasses = grouped ? `border-top p-1 ${bg}` : `border border-2 border-secondary rounded p-1 mb-2 ${bg}`;

  return (
    <div className={rowClasses}>
      <div style={{ display: 'grid', gridTemplateColumns: 'min-content 1fr min-content 1fr', justifyContent: 'center' }}>
        <div className="text-center align-self-start" style={{ fontSize: '24px', fontWeight: 'bold' }}>{index || ""}</div>

        <ClusterColumn isLeft={true} summary={leftSummary} hideModel={index === null} color="bg-light-danger" topImages={leftImages} compareImages={rightImages} />

        <div className="text-center">
          <SimBadge value={similarity} />

          <br />

          <strong style={{ fontSize: '2rem' }}>→</strong>

          <br />

          <label className="mb-0 mr-1 no-wrap">
            <input type="checkbox" checked={selected} onChange={() => onToggle(rowKey)} />
            <span className="ml-1 font-weight-bold">Merge</span>
          </label>

          <IconButton
            icon="image_search"
            level="violeta"
            description="See images similar to the first image on the left cluster"
            onClick={() => openSimilarImagesModal(leftImages[0].img.imgId)}
          >
            Similar
          </IconButton>

          <IconButton
            icon="scatter_plot"
            level="secondary"
            className="mt-1"
            description="PCA scatter of both clusters' embeddings, points colored per cluster"
            onClick={openPcaGraphModal}
          >
            PCA graph
          </IconButton>

          {headerExtras}
        </div>

        <ClusterColumn summary={rightSummary} color="bg-light-success" topImages={rightImages} compareImages={leftImages} />
      </div>
    </div>
  );
});

// Stable empty array so `result?.x || EMPTY_ARR` keeps a constant identity across renders
// (a fresh `[]` would invalidate every downstream useMemo on each render).
const EMPTY_ARR = [];

// Always fold the smaller cluster into the larger one (used for every same-model merge).
// Returns the actual survivor/removed clusters so display and merge op can never disagree.
// Pure + prop-independent, so hoisted to module scope to keep them out of memo deps.
const orderForMerge = (x, y) => ((x.totalImages || 0) <= (y.totalImages || 0) ? { from: x, into: y } : { from: y, into: x });
const mergeSmallerIntoLarger = (x, y) => {
  const { from, into } = orderForMerge(x, y);
  return { fromId: from.clusterId, intoId: into.clusterId };
};

// Duplicate pairs are always same-model → smaller merges onto larger.
const dupMergeOp = (pair) => mergeSmallerIntoLarger(pair.clusterA, pair.clusterB);

// Same-model suggestion: keep the bigger cluster as the survivor. Cross-model: move the
// isolated image's cluster into the suggested (other-model) cluster.
const wrongMergeOp = (w) => (
  w.sameModel
    ? mergeSmallerIntoLarger(w.sourceCluster, w.suggestedCluster)
    : { fromId: w.sourceCluster.clusterId, intoId: w.suggestedCluster.clusterId }
);

const dupKey = (i) => `dup-${i}`;
const wmKey = (i) => `wm-${i}`;

export default function ModalFindDuplicateClusters({ result, onSave, onCancel }) {
  const { page } = useContext(LegoAdminPageContext);

  const duplicatePairs = result?.duplicatePairs || EMPTY_ARR;
  const crossModelImages = result?.crossModelImages || EMPTY_ARR;

  // Unified selection across both sections, keyed by a stable per-suggestion key.
  const [selected, setSelected] = useState(() => new Set());

  // Merge op for every suggestion, keyed. The FROM (removed) cluster id is both the
  // grouping key and the contradiction key: a cluster can only be merged once. Memoized so
  // the keyed ops — and every descriptor/callback derived from them — keep stable identities
  // across the frequent re-renders triggered by toggling checkboxes, letting the memoized
  // rows below skip re-rendering.
  const opByKey = useMemo(() => {
    const m = {};
    duplicatePairs.forEach((pair, i) => { m[dupKey(i)] = dupMergeOp(pair); });
    crossModelImages.forEach((w, i) => { m[wmKey(i)] = wrongMergeOp(w); });
    return m;
  }, [duplicatePairs, crossModelImages]);

  // Suggestions that remove the same FROM cluster are mutually exclusive.
  const keysByFromId = useMemo(() => _.groupBy(Object.keys(opByKey), k => opByKey[k].fromId), [opByKey]);

  const toggleSelected = useCallback((key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Selecting a merge for a cluster clears any other (contradictory) merge of it.
        (keysByFromId[opByKey[key].fromId] || []).forEach(k => next.delete(k));
        next.add(key);
      }
      return next;
    });
  }, [keysByFromId, opByKey]);

  const plannedOps = _.uniqBy(
    [...selected].map(k => opByKey[k]).filter(Boolean),
    o => `${o.fromId}->${o.intoId}`,
  );

  const applyMerges = () => {
    if (!plannedOps.length) {
      page.alert('Nothing marked for merge.');
      return;
    }
    page.runAsync(async () => {
      const opsSvc = page.service('/services/data/image-clusters/operations');
      const failures = [];
      for (const op of plannedOps) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await opsSvc.create({ operation: 'merge', fromId: op.fromId, intoId: op.intoId });
          clearCacheByUrlAndId('services/data/image-clusters', op.fromId);
          clearCacheByUrlAndId('services/data/image-clusters', op.intoId);
        } catch (err) {
          failures.push(`${op.fromId} → ${op.intoId}: ${err.message}`);
        }
      }
      if (failures.length) {
        page.alert(`Some merges failed:\n${failures.join('\n')}`);
      }
      onSave();
    }, `Merging ${plannedOps.length} cluster pair(s)...`);
  };

  const { stats = {}, thresholds = {} } = result || {};

  // Display descriptors, grouped per section by the FROM cluster so contradictory
  // merges of the same cluster render together as one pick-at-most-one block.
  const dupDescriptors = useMemo(() => duplicatePairs.map((pair, i) => {
    const op = opByKey[dupKey(i)];
    // Always lay out FROM (removed) on the left, INTO (survivor) on the right.
    const fromIsA = op.fromId === pair.clusterA.clusterId;
    const fromSummary = fromIsA ? pair.clusterA : pair.clusterB;
    const intoSummary = fromIsA ? pair.clusterB : pair.clusterA;
    const fromImages = _.map(pair.topPairs, p => ({ img: fromIsA ? p.a : p.b, similarity: p.similarity, median: fromIsA ? p.medianA : p.medianB }));
    const intoImages = _.map(pair.topPairs, p => ({ img: fromIsA ? p.b : p.a, similarity: p.similarity, median: fromIsA ? p.medianB : p.medianA }));
    return {
      key: dupKey(i), fromId: op.fromId, defaultBg: 'bg-light', sameModel: true,
      leftSummary: fromSummary, leftImages: fromImages,
      rightSummary: intoSummary, rightImages: intoImages,
      similarity: pair.similarity,
      headerExtras: pair.detectedVia === 'global-search'
        ? <span className="badge badge-info ml-2" title="Small cluster matched against the global index; the other cluster may be outside the current filter">via global search</span>
        : null,
    };
  }), [duplicatePairs, opByKey]);


  const wmDescriptors = useMemo(() => crossModelImages.map((w, i) => {
    const op = opByKey[wmKey(i)];
    return {
      key: wmKey(i), fromId: op.fromId, defaultBg: 'bg-light-warning', sameModel: w.sameModel,
      leftSummary: w.sourceCluster, leftImages: _.map(w.images, im => ({ img: im, similarity: im.similarity, median: im.median })),
      rightSummary: w.suggestedCluster, rightImages: _.map(w.topPairs, p => ({ img: p.b, similarity: p.similarity, median: p.medianB })),
      similarity: w.similarity,
      headerExtras: !w.sameModel ? <>
        <span className="badge badge-warning mt-2 mb-2 mr-2">DIFFERENT MODEL</span>
      </> : null,
    };
  }), [crossModelImages, opByKey]);

  // Both kinds of suggestion grouped together by FROM cluster: a cluster suggested for both a
  // duplicate (same-model) merge and a wrong-model move is contradictory (it can only merge
  // once), so they belong in one "choose at most one" block, not two separate sections.
  const allDescriptors = useMemo(() => [...dupDescriptors, ...wmDescriptors], [dupDescriptors, wmDescriptors]);

  const renderRow = (d, grouped, index) => (
    <MergeRow
      key={d.key}
      rowKey={d.key}
      selected={selected.has(d.key)}
      onToggle={toggleSelected}
      grouped={grouped}
      defaultBg={d.defaultBg}
      headerExtras={d.headerExtras}
      index={index}
      leftSummary={d.leftSummary} leftImages={d.leftImages}
      rightSummary={d.rightSummary} rightImages={d.rightImages}
      similarity={d.similarity}
    />
  );

  // Best similarity among a group's items that match `pred` (null if none match).
  const bestSimilarity = (items, pred) => {
    const sims = items.filter(pred).map(d => (d.similarity != null ? d.similarity : 0));
    return sims.length ? Math.max(...sims) : null;
  };

  const renderGroups = (descriptors) => {
    // Order groups by the strength of their best *in-cluster* (same-model) option first;
    // groups that have no same-model option at all fall behind them, ranked by their best
    // cross-model option. So the safest, most-confident within-cluster merges surface on top.
    const groups = _.orderBy(
      groupByFromOrdered(descriptors).map(g => ({ ...g, bestSame: bestSimilarity(g.items, d => d.sameModel) })),
      [g => (g.bestSame != null ? 1 : 0), g => (g.bestSame != null ? g.bestSame : bestSimilarity(g.items, d => !d.sameModel))],
      ['desc', 'desc'],
    );

    return groups.map((g, i) => {
      // Within a group, the most-similar candidate wins. Show candidates from the top
      // until similarity falls off a cliff (>0.1 drop between consecutive ones), then
      // collapse the clearly-worse rest behind a "See K more candidates" toggle — but
      // always keep at least one same-model option visible (see splitVisibleCollapsed).
      const sameModelPenalty = d => d.sameModel ? 0 : -0.02;
      const sorted = _.orderBy(g.items, d => (d.similarity != null ? sameModelPenalty(d) + d.similarity : 0), 'desc');
      const multi = sorted.length > 1;
      const { visible, collapsed } = splitVisibleCollapsed(sorted);
      return <GroupBlock
        key={`g-${g.fromId}`}
        multi={multi}
        leftId={g.fromId}
        totalCount={sorted.length}
        visible={visible.map((d, j) => renderRow(d, multi, j == 0 ? i + 1 : null))}
        collapsed={collapsed.map((d) => renderRow(d, multi, null))}
      />;
    });
  };

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-3 p-2 bg-light rounded">
        <span className="mr-3">
          Analyzed <strong>{stats.clustersAnalyzed}</strong> clusters · {stats.imagesEmbedded} embedded images ·
          checked {stats.smallClustersChecked} small clusters
        </span>
        <span className="small text-muted">
          duplicate ≥ {thresholds.minPairSimilarity} · wrong-model ≥ {thresholds.smallClusterCrossModelMinSimilarity} (clusters ≤ {thresholds.smallClusterMaxImages} imgs)
        </span>
        <span className="ml-auto">
          <button className="btn btn-primary mr-2" onClick={applyMerges} disabled={!plannedOps.length}>
            Apply {plannedOps.length} merge{plannedOps.length === 1 ? '' : 's'}
          </button>
          <button className="btn btn-secondary" onClick={onCancel}>Close</button>
        </span>
      </div>

      {/* SUGGESTED MERGES (duplicates + wrong-model, grouped per FROM cluster) -- */}
      <h5>
        Suggested merges <span className="badge badge-secondary">{duplicatePairs.length + crossModelImages.length}</span>
        <span className="small text-muted ml-2">{duplicatePairs.length} duplicate · {crossModelImages.length} wrong-model</span>
      </h5>
      {!allDescriptors.length && <div className="text-muted mb-3">No duplicate or wrong-model suggestions found.</div>}
      {renderGroups(allDescriptors)}
    </div>
  );
}
